<template>
<div class="editor">
  
  <div v-if="editor" class="toolbar">
    
    <template v-if="editContextAllows.typography">
      
      <div class="button-set">
        <button 
          @click="editor.chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }">
          <ri n="bold" />
        </button>
        <button 
          @click="editor.chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }">
          <ri n="italic" />
        </button>
        <VueCustomTooltip label="Clean up formatting (useful when pasting from Word, email, websites, etc) - this will NOT remove bold, italics, or headings" position="is-right">
          <button @click="editor.chain().focus().clearFormatting().removeEmptyNodes().run()">
            <ri n="format-clear" />
          </button>
        </VueCustomTooltip>
      </div>
      
      <div class="button-set">
        <div class="button-set">
          <button
            @click="toggleSubmenu('heading')"
            :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }">
            <ri n="h-6" v-if="editor.isActive('heading', { level: 6 })" />
            <ri n="h-5" v-if="editor.isActive('heading', { level: 5 })" />
            <ri n="h-4" v-if="editor.isActive('heading', { level: 4 })" />
            <ri n="h-3" v-if="editor.isActive('heading', { level: 3 })" />
            <ri n="h-2" v-if="editor.isActive('heading', { level: 2 })" />
            <ri n="h-1" v-if="editor.isActive('heading', { level: 1 })" />
            <ri n="paragraph" v-if="!editor.isActive('heading')" />
            
          </button>
          
          <div class="submenu" :class="submenu === 'heading' ? 'shown' : ''">
            <button @click="editor.chain().focus().setParagraph().run()" :class="{ 'is-active': editor.isActive('paragraph') }">
              <ri n="paragraph" />
            </button>
            <button v-for="level in [1,2,3,4,5,6]" :key="level"
                    @click="editor.chain().focus().toggleHeading({ level }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level }) }">
              <ri :n="`h-${level}`" />
            </button>
          </div>
        </div>
        <button @click="toggleSubmenu('font-size')">
          <ri n="font-size-2" />
        </button>
        
        <div class="submenu" :class="submenu === 'font-size' ? 'shown' : ''">
          <button @click="editor.chain().focus().setFontSize( '100%' ).run()" style="font-size:10px">
            1x
          </button>
          <button @click="editor.chain().focus().setFontSize( '150%' ).run()" style="font-size:10px">
            1.5x
          </button>
          <button @click="editor.chain().focus().setFontSize( '200%' ).run()" style="font-size:10px">
            2x
          </button>
          <button @click="editor.chain().focus().setFontSize( '400%' ).run()" style="font-size:10px">
            4x
          </button>
          <button @click="editor.chain().focus().setFontSize( '12px' ).run()" style="font-size:10px">
            12px
          </button>
          <button @click="editor.chain().focus().setFontSize( '14px' ).run()" style="font-size:10px">
            14px
          </button>
          <button @click="editor.chain().focus().setFontSize( '16px' ).run()" style="font-size:10px">
            16px
          </button>
          <button @click="editor.chain().focus().setFontSize( '24px' ).run()" style="font-size:10px">
            24px
          </button>
          <button @click="editor.chain().focus().setFontSize( '36px' ).run()" style="font-size:10px">
            36px
          </button>
          <button @click="editor.chain().focus().unsetFontSize().run()" style="font-size:10px">
            Auto
          </button>
        </div>
      </div>
      
      <div class="button-set">
        <button>
          <color-picker @input="editor.chain().focus().setColor($event).run()" fallback="black" />
        </button>
        <VueCustomTooltip label="Unset text color (and use default instead)" position="is-right">
          <button @click="editor.chain().focus().unsetColor().run()">
            <div class="color-container">
              <div class="color-preview">
                <div class="color-checkerboard"></div>
              </div>
            </div>
          </button>
        </VueCustomTooltip>
      </div>
      
      <div class="button-set">
        <VueCustomTooltip label="Insert hyperlink" position="is-top">
          <button 
            @click="setInternalLink">
            <ri n="link" />
          </button>
        </VueCustomTooltip>
        
        <VueCustomTooltip label="Insert button" position="is-top">
          <button style="width: 30px" v-html="icons.button" @click="addButtons"></button>
        </VueCustomTooltip>
              
      </div>
      
      <div class="button-set">
        
        <button @click="toggleSubmenu('list')">
          <ri n="list-unordered" />
        </button>
        
        <div class="submenu" :class="submenu === 'list' ? 'shown' : ''">
          <button 
            @click="editor.chain().focus().toggleBulletList().run()"
            :class="{ 'is-active': editor.isActive('bulletList') }">
            <ri n="list-unordered" />
          </button>
          <button 
            @click="editor.chain().focus().toggleOrderedList().run()"
            :class="{ 'is-active': editor.isActive('orderedList') }">
            <ri n="list-ordered" />
          </button>
        </div>
      </div>
            
      <div class="button-set">
        
        <button @click="toggleSubmenu('alignment')">
          <ri v-if="editor.isActive({ textAlign: 'right' })" n="align-right" />
          <ri v-else-if="editor.isActive({ textAlign: 'center' })" n="align-center" />
          <ri v-else-if="editor.isActive({ textAlign: 'left' })" n="align-left" />
          <ri v-else-if="editor.isActive({ textAlign: 'justify' })" n="align-justify" />
          <ri v-else n="align-left" />
        </button>
        
        <div class="submenu" :class="submenu === 'alignment' ? 'shown' : ''">      
          <button
            :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
            @click="editor.chain().focus().setCustomTextAlign('left').updateBlockBoxLayout({ float: 'left', marginLeft: 0, marginRight: '1rem' }).run()">
            <ri n="align-left" />
          </button>
          <button
            :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"            
            @click="editor.chain().focus().setCustomTextAlign('center').updateBlockBoxLayout({ float: null, marginLeft: 'auto', marginRight: 'auto' }).run()">
            <ri n="align-center" />
          </button>
          <button
            :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }"
            @click="editor.chain().focus().setCustomTextAlign('right').updateBlockBoxLayout({ float: 'right', marginLeft: '1rem', marginRight: 0 }).run()">
            <ri n="align-right" />
          </button>
          <button
            :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }"
            @click="editor.chain().focus().setTextAlign('justify').updateBlockBoxLayout({ float: null, marginLeft: 'auto', marginRight: 'auto' }).run()">
            <ri n="align-justify" />
          </button>
        </div>
      </div>
      
      <button 
              @click="editor.chain().focus().setHorizontalRule().run()">
        <ri n="separator" />
      </button>
    </template>

    <div v-if="editContextAllows.blockAlignment" class="button-set">
      
      <button @click="toggleSubmenu('alignment')">
        <div style="width: 26px;" v-if="editor.isActive({ float: 'left' })" v-html="icons.float.left"></div>
        <div style="width: 26px;" v-else-if="editor.isActive({ float: 'right' })" v-html="icons.float.right"></div>          
        <div style="width: 26px;" v-else v-html="icons.float.none">Center</div>
      </button>
      
      <div class="submenu" :class="submenu === 'alignment' ? 'shown' : ''">      
        <button style="width: 100%; margin-top: -2px;"
                @click="editor.chain().focus().updateBlockBoxLayout({ float: 'left', marginLeft: 0, marginRight: '1rem', maxWidth: '450px' }).run()"
                :class="{ 'is-active': editor.isActive({ float: 'left' }) }"
                v-html="icons.float.left">
        </button>
        <button style="width: 100%; margin-top: -2px;" @click="editor.chain().focus().updateBlockBoxLayout({ float: null, marginLeft: 'auto', marginRight: 'auto', 'display': 'block' }).run()"
                :class="{ 'is-active':  !editor.isActive({ float: 'left' }) && !editor.isActive({ float: 'right' }) }"
                v-html="icons.float.none">
        </button>
        <button style="width: 100%; margin-top: -3px;" @click="editor.chain().focus().updateBlockBoxLayout({ float: 'right', marginLeft: '1rem', marginRight: 0 }).run()" v-html="icons.float.right"
                :class="{ 'is-active': editor.isActive({ float: 'right' }) }">
        </button>
      </div>
    </div>

    <div v-if="editContextAllows.boxLayout" class="button-set">
      <button @click="toggleSubmenu('max-width')">
        <img width="26" src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iNzUycHQiIGhlaWdodD0iNzUycHQiIHZlcnNpb249IjEuMSIgdmlld0JveD0iMCAwIDc1MiA3NTIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiA8cGF0aCBkPSJtNDkzLjg2IDM2Mi4yNy0xNy43NS0xNy43NDZjLTUuMzU5NC01LjM1OTQtNS4zNTk0LTE0LjA1MSAwLTE5LjQxNCA1LjM2MzMtNS4zNTk0IDE0LjA1NS01LjM1OTQgMTkuNDE0IDBsNDEuMTggNDEuMTg0YzUuMzYzMyA1LjM1OTQgNS4zNjMzIDE0LjA1MSAwIDE5LjQxbC00MS4xOCA0MS4xODRjLTUuMzU5NCA1LjM1OTQtMTQuMDUxIDUuMzU5NC0xOS40MTQgMC01LjM1OTQtNS4zNjMzLTUuMzU5NC0xNC4wNTUgMC0xOS40MTRsMTcuNzUtMTcuNzVoLTIzNS43MWwxNy43NDYgMTcuNzVjNS4zNjMzIDUuMzU5NCA1LjM2MzMgMTQuMDUxIDAgMTkuNDE0LTUuMzU5NCA1LjM1OTQtMTQuMDUxIDUuMzU5NC0xOS40MSAwbC00MS4xODQtNDEuMTg0Yy01LjM1OTQtNS4zNTk0LTUuMzU5NC0xNC4wNTEgMC0xOS40MWw0MS4xODQtNDEuMTg0YzUuMzU5NC01LjM1OTQgMTQuMDUxLTUuMzU5NCAxOS40MSAwIDUuMzYzMyA1LjM2MzMgNS4zNjMzIDE0LjA1NSAwIDE5LjQxNGwtMTcuNzQ2IDE3Ljc0NnptLTM1MS4yMS0xNzguNDVjMC03LjU4MiA2LjE0NDUtMTMuNzI3IDEzLjcyNy0xMy43MjcgNy41NzgxIDAgMTMuNzI3IDYuMTQ0NSAxMy43MjcgMTMuNzI3djM4NC4zNmMwIDcuNTgyLTYuMTQ4NCAxMy43MjctMTMuNzI3IDEzLjcyNy03LjU4MiAwLTEzLjcyNy02LjE0NDUtMTMuNzI3LTEzLjcyN3ptNDM5LjI2IDBjMC03LjU4MiA2LjE0NDUtMTMuNzI3IDEzLjcyNy0xMy43MjcgNy41ODIgMCAxMy43MjcgNi4xNDQ1IDEzLjcyNyAxMy43Mjd2Mzg0LjM2YzAgNy41ODItNi4xNDQ1IDEzLjcyNy0xMy43MjcgMTMuNzI3LTcuNTgyIDAtMTMuNzI3LTYuMTQ0NS0xMy43MjctMTMuNzI3eiIvPgo8L3N2Zz4K">
      </button>

      <div class="submenu" :class="submenu === 'max-width' ? 'shown' : ''">
        <button @click="editor.chain().focus().updateBoxLayout({ maxWidth: '25%' }).run()" style="font-size:10px">
          25%
        </button>
        <button @click="editor.chain().focus().updateBoxLayout({ maxWidth: '50%' }).run()" style="font-size:10px">
          50%
        </button>
        <button @click="editor.chain().focus().updateBoxLayout({ maxWidth: '75%' }).run()" style="font-size:10px">
          75%
        </button>
        <button @click="editor.chain().focus().updateBoxLayout({ maxWidth: '100%' }).run()" style="font-size:10px">
          100%
        </button>
        <button @click="editor.chain().focus().updateBoxLayout({ maxWidth: null }).run()" style="font-size:10px">
          Auto
        </button>
      </div>
    </div>

    <div class="button-set" v-if="editContextAllows.elevation">
      <button @click="toggleSubmenu('elevation')">
        <VueCustomTooltip label="Elevation (Shadow)">
          <ri n="add-line" style="box-shadow: 0 0 10px black" />
        </VueCustomTooltip>
      </button>
    
      <div class="submenu submenu-wide" :class="submenu === 'elevation' ? 'shown' : ''">
        <button v-for="{ value, label } in elevations" :key="label"
                :class="{ 'is-active': editor.isActive({ boxShadow: value }) }"
                @click="editor.chain().focus().setElevation(value).run()" style="font-size:10px">
          {{ label }}
        </button>
      </div>
    </div>

    <div v-if="editContextAllows.insertion"
         class="button-set" @mouseover="submenu = 'add-image'" @mouseout="submenu = null">
      <button @click="addImageFromLibrary">
        <VueCustomTooltip label="Insert image" position="is-left">              
          <ri n="image-add-line" />
        </VueCustomTooltip>        
      </button>
    </div>
    
    <button v-if="editContext === 'image'"
            @click="editImage">
      <VueCustomTooltip label="Crop or edit image" position="is-left">
        <ri n="image-edit-line" />
      </VueCustomTooltip>
    </button>

    <div class="button-set">
      <VueCustomTooltip v-if="editContextAllows.insertion" label="Insert form" position="is-top">
        <button style="width: 30px"
                @click="toggleSubmenu('add-form')" v-html="icons.form" >
        </button>
      </VueCustomTooltip>

      <div class="submenu submenu-wide" :class="submenu === 'add-form' ? 'shown' : ''">
        <button v-for="form in forms" :key="form"
                @click="addForm(form)"
                style="font-size: 10px; text-transform: uppercase">
          {{ form.replace(/-/g, ' ') }}
        </button>
      </div>

      <VueCustomTooltip v-if="editContextAllows.insertion" label="Insert custom element" position="is-top">      
        <button style="width: 30px" @click="toggleSubmenu('add-user-defined-component')"
                v-html="icons.component">
        </button>
      </VueCustomTooltip>

      <div class="submenu submenu-wide" :class="submenu === 'add-user-defined-component' ? 'shown' : ''">
        <button v-for="component in userDefinedComponents" :key="component"
                @click="addUserDefinedComponent(component)"
                style="font-size: 10px; text-transform: uppercase">
          {{ component.replace(/-/g, ' ') }}
        </button>
      </div>

      <VueCustomTooltip v-if="Object.keys(mergeVars || {}).length" label="Insert merge variable" position="is-left">      
        <button @click="toggleSubmenu('insert-merge-var')">
          <ri n="braces-line" />
        </button>
      </VueCustomTooltip>

      <div class="submenu submenu-wide" :class="submenu === 'insert-merge-var' ? 'shown' : ''">
        <button v-for="{ label, code }, index in (mergeVars || {})" :key="index"
                style="font-size: 10px; text-transform: uppercase"
                @click="editor.chain().focus().insertContent(code).run()">
          {{ label }}
        </button>
      </div>
      
    </div>

    <div class="button-set">
      <VueCustomTooltip label="Undo" position="is-left">      
        <button @click="editor.chain().focus().undo().run()">
          <ri n="arrow-go-back-line" />
        </button>
      </VueCustomTooltip>
      <VueCustomTooltip label="Redo" position="is-left">      
        <button @click="editor.chain().focus().redo().run()">
          <ri n="arrow-go-forward-line" />
        </button>
      </VueCustomTooltip>        
    </div>
    
  </div>
  
  <editor-content :editor="editor"
                  :class="{'box-layout-selecting' : boxLayoutSelection.selecting}" />
</div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import { Node, Extension, mergeAttributes } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Color from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import { Plugin, PluginKey } from 'prosemirror-state'
import Vue from 'vue';
import v4 from 'uuid/v4'
//import { get } from 'axios';
import CloudinaryImageEditor from './CloudinaryImageEditor.vue'
//import CssValueInput from './inputs/CssValueInput.vue';
import BoxLayout from './dialogs/BoxLayout.vue'
import DialogAddUserDefinedComponent from './DialogAddUserDefinedComponent.vue';
import DialogAddLink from './DialogAddLink.vue';
import DialogAddPButtons from './DialogAddPButtons.vue';
import ImageLibrary from './ImageLibrary.vue'
import { openDialog } from '../modal-dialog.js';
import { kebabCase } from 'lodash';
import ColorPicker from './ColorPicker.vue';
import FontSize from '@/tiptap/font-size.js';

const BoxLayoutPluginKey = new PluginKey('box-layout-activation');

let boxLayoutSelection = Vue.observable({
  selecting: false,
  selected: null,
});

const makeAttr = attr => ({
  [attr]: {
    default: null,
    parseHTML: element => element.style[attr],
    renderHTML: attrs => attrs[attr] ? { style: `${kebabCase(attr)}: ${attrs[attr]}` } : null,
  }
});

const boxLayoutAttributeNames = [
  'backgroundColor', 'color', 'borderTopLeftRadius', 'borderTopRightRadius',
  'borderBottomLeftRadius', 'borderBottomRightRadius',
  'width', 'maxWidth', 'minWidth',
  'height', 'maxHeight', 'minHeight',
  'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'float', 'display',
  'boxShadow'
];

const boxLayoutAttributes = Object.assign(
  ...boxLayoutAttributeNames.map(o => makeAttr(o))
);

const BoxLayoutExtension = Extension.create({
  name: 'boxLayout',
  
  defaultOptions: {
    types: [],
    defaultPadding: null,
    defaultMargin: null,
  },
  
  addProseMirrorPlugins () {
    return [
      new Plugin({
        props: {
          handleClickOn(view, pos, node) {
            if (!boxLayoutSelection.selecting) {
              return;
            }
            boxLayoutSelection.selected = node;
            boxLayoutSelection.selecting = false;
          },
        },
        key: BoxLayoutPluginKey,
      }),
    ];
  },
  
  addGlobalAttributes () {
    return [
      {
        types: this.options.types,
        attributes: boxLayoutAttributes,
      },
    ];
  },
  
  addCommands () {
    return {
      updateBoxLayout: (box) => ({ commands }) => {
        return this.options.types.every(type => commands.updateAttributes(type, box));
      },
      setElevation: (elevation) => ({ commands }) => {
        return this.options.types.every(type => commands.updateAttributes(type, { boxShadow: elevation }));
      },
      updateBlockBoxLayout: (box) => ({ commands }) => {
        return this.options.blockTypes.every(type => commands.updateAttributes(type, box));
      },
    };
  },
  
});

const PButtons = Node.create({
  name: 'p-buttons',
  content: 'text*',
  group: 'block',
  marks: '',
  atom: true,
  draggable: true,
  selectable: true,
  
  addAttributes () {
    let attrs = {};
    attrs[':links'] = { default: [] };
    attrs['size'] = { default: null };
    attrs['button-color'] = { default: null };
    attrs['button-text-color'] = { default: null };
    attrs['button-color__hover'] = { default: null };
    attrs['button-text-color__hover'] = { default: null };
    return attrs;
  },
  
  parseHTML () {
    return [
      {
        tag: 'p-buttons',
        getAttrs: node => {
          let attrs = {};
          attrs[':links'] = node[':links'];
          attrs['size'] = node.style.getPropertyValue('--button-width');
          attrs['button-color'] = node.style.getPropertyValue('--button-color');
          attrs['button-text-color'] = node.style.getPropertyValue('--button-text-color');
          attrs['button-color__hover'] = node.style.getPropertyValue('--button-color__hover');
          attrs['button-text-color__hover'] = node.style.getPropertyValue('--button-text-color__hover');
          return attrs;
        },
      },
    ];
  },
  
  renderHTML ({ HTMLAttributes }) {
    let attrs = mergeAttributes(HTMLAttributes);
    let style = [];
    if (attrs['size']) {
      style.push(`--button-width: ${attrs['size']}`);
    }
    if (attrs['button-color']) {
      style.push(`--button-color: ${attrs['button-color']}`);
    }
    if (attrs['button-text-color']) {
      style.push(`--button-text-color: ${attrs['button-text-color']}`);
    }
    if (attrs['button-color__hover']) {
      style.push(`--button-color__hover: ${attrs['button-color__hover']}`);
    }
    if (attrs['button-text-color__hover']) {
      style.push(`--button-text-color__hover: ${attrs['button-text-color__hover']}`);
    }
    if (style.length) {
      attrs['style'] = style.join(';');
    }
    
    return ['p-buttons', attrs];
  },
  
  
  addCommands () {
    return {
      [`setPButtons`]: options => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            ...options,
          },
        });
      },
    };
  },
  
  addNodeView () {
    return ({ HTMLAttributes }) => {

      let props = '';
      Object.keys(HTMLAttributes).forEach(key => {
        let val = HTMLAttributes[key];
        if (typeof val === 'string' && !val.startsWith('"') && !val.startsWith("'")) {
          if (val.indexOf('"') !== -1) {
            val = `'${val}'`;
          } else {
            val = `"${val}"`;
          }
        }
        props += `${key}=${val} `;
      });
      
      const cmp = Vue.compile(`<p-buttons ${props}></p-buttons>`);
      let el = (new Vue({
        render: cmp.render,
      })).$mount().$el;

      el.setAttribute("data-type", "custom-component--p-buttons");
      el.addEventListener('click', e => e.preventDefault());
      return {
        dom: el,
      };

      /*
      const container = document.createElement('div');
      container.setAttribute("data-type", `custom-component--p-buttons`);
      Object.keys(HTMLAttributes).forEach(key => {
        container.setAttribute(key, HTMLAttributes[key]);
      });      
      container.style.width = '100%';
      container.style.backgroundColor = '#eee';
      container.style.height = '200px';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
      let links = HTMLAttributes[':links'];
      if (typeof links === 'string') {
        links = JSON.parse(links);
      }
      container.innerHTML = (links || [])
        .map(({ text }) => `<div class="wp-block-button"><a href="#" class="wp-block-button__link">${text}</a></div>`)
        .join('');
      
      return {
        dom: container,
      };
      */
    };
  },
  
});

const UserDefinedComponentFactory = (name, props) => {
  return Node.create({
    name,
    content: 'text*',
    group: 'block',
    marks: '',
    atom: true,
    draggable: true,
    selectable: true,
    
    addAttributes () {
      let attrs = {};
      props.forEach(key => {
        attrs[key] = { default: '' };
      });
      attrs['uuid'] = { default: '' };
      return attrs;
    },
    
    parseHTML () {
      return [
        {
          tag: name,
          getAttrs: node => {
            let attrs = {};
            attrs['uuid'] = node['uuid'];
            props.forEach(key => {
              attrs[key] = node[key];
            });
            return attrs;
          },
        },
      ];
    },
    
    renderHTML ({ HTMLAttributes }) {
      let attrs = mergeAttributes(HTMLAttributes);
      return [name, attrs];
    },
    
    addCommands () {
      return {
        [`setUserDefinedComponent-${name}`]: options => ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...options,
              uuid: v4(),
            },
          });
        },
      };
    },
    
    addNodeView () {
      return ({ HTMLAttributes }) => {
        const container = document.createElement('div');
        container.setAttribute("data-type", `custom-component--${name}`);
        Object.keys(HTMLAttributes).forEach(key => {
          container.setAttribute(key, HTMLAttributes[key]);
        });      
        container.style.width = '100%';
        container.style.backgroundColor = '#eee';
        container.style.height = '200px';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.innerHTML = `<div><h2 style="text-align: center">A ${name.replace(/-/g, ' ')}</h2></div>`;
        
        return {
          dom: container,
        };
      };
    },
  });
};

const CustomTextAlign = TextAlign.extend({
  addCommands() {
    return {
      setCustomTextAlign: (alignment) => ({ commands }) => {
        return this.options.types.every(type => commands.updateAttributes(type, { textAlign: alignment }));        
      },
      setTextAlign: (alignment) => ({ commands }) => {
        if (!this.options.alignments.includes(alignment)) {
          return false
        }

        return this.options.types.every(type => commands.updateAttributes(type, { textAlign: alignment }))
      },
      unsetTextAlign: () => ({ commands }) => {
        return this.options.types.every(type => commands.resetAttributes(type, 'textAlign'))
      },
    }
  },
});

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      float: {
        default: null,
        parseHTML: element => element.style.float || null,
        renderHTML: attrs => {
          return {
            style: attrs.float !== null ? `float: ${attrs.float}` : null,
          };
        },
      },
      shadow: {
        default: 'none',
        parseHTML: el => [...el.classList].map(o => o.match(/^shadow-(?<shadow>[a-z]+)/)?.groups?.shadow).filter(o => o)[0] || 'none',
        renderHTML: attrs => {
          return {
            'class': attrs.shadow && attrs.shadow !== 'none' ? `shadow-${attrs.shadow}` : '',
          };
        },
      },
    }
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setImageFloat: (alignment) => ({ commands }) => {
        return commands.updateAttributes('image', { float: alignment})
      },
      setImageShadow: (shadow) => ({ commands }) => {
        return commands.updateAttributes('image', { shadow: shadow})
      },
    }
  },
});

const ClearFormatting = Extension.create({
  name: 'clearFormatting',

  defaultOptions: {
    preserveMarks: ['link', 'bold', 'underline'],
    cleanNodes: ['heading', 'paragraph'],
    removeEmptyNodes: ['paragraph', 'heading', 'listItem', 'strong', 'b', 'i', 'em'],
  },
  
  addCommands () {
    return {
      ...this.parent?.(),

      clearFormatting: () => ({ commands, tr, state, dispatch }) => {
        const { selection } = tr;
        const { empty, ranges } = selection;

        if (empty) {
          return true;
        }

        if (dispatch) {
          Object
            .entries(state.schema.marks)
            .forEach(([, mark]) => {
              if (this.options.preserveMarks.indexOf(mark.name) !== -1) {
                return;
              }
              ranges.forEach(range => {
                tr.removeMark(range.$from.pos, range.$to.pos, mark)
              })
            })
          this.options.cleanNodes.every(type => commands.updateAttributes(
            type, boxLayoutAttributeNames.reduce((acc, o) => ({ ...acc, [o]: null }), {})
          ));
        }

        return true
      },

      removeEmptyNodes: () => ({ tr, state, dispatch }) => {

        if (dispatch) {
          tr.selection.ranges.forEach(range => {
            const from = range.$from.pos
            const to = range.$to.pos

            window.tr = tr;
            window.state = state;

            let toDelete = [];
            
            state.doc.nodesBetween(from, to, (node, pos, parent) => {
              if (this.options.removeEmptyNodes.indexOf(node.type?.name) !== -1) {
                if (node.content?.size === 0 || (node.content?.size === 1 && node.content[0]?.text === ' ')) {
                  if (parent.type?.name === 'doc') {
                    toDelete.push(pos);
                  }
                  if (Object.values(node.attrs).filter(o => o).length === 0) {
                    window.deletingNode = node;
                  }
                }
                else {
                  console.log(node.content);
                }
              }
            });

            toDelete.reverse().forEach(pos => {
              tr.delete(pos, pos + 1);
            });
          });
        }
        return true;
      },
    };
  },
});

export default {
  name: 'Tiptap',

  components: {
    EditorContent,
    ColorPicker,
    //CssValueInput,
  },
  
  props: {
    value: String,
    mergeVars: Array,
  },

  computed: {

    userDefinedComponents () {
      const components = this.$store.state.repo.config.components;
      let keys = Object.keys(components).filter(key => components[key].type === 'user-defined');
      return keys;
    },
    
    forms () {
      const components = this.$store.state.repo.config.components;
      return Object.keys(components).filter(key => components[key].type === 'form');
    },

    icons: () => ({
      button: `

<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60" x="0px" y="0px"><path d="M49,0H11A11.013,11.013,0,0,0,0,11v6A11.013,11.013,0,0,0,11,28H28V40.063a4.392,4.392,0,0,1-.974-2.172A3.466,3.466,0,0,0,23.593,35a3.646,3.646,0,0,0-3.575,3.979,15.417,15.417,0,0,0,4.826,10.249l.423.452c.589.636,1.152,1.176,1.7,1.7C28.792,53.136,30,54.3,30,57a3,3,0,0,0,3,3H45a3,3,0,0,0,3-3V54c0-2.917.348-3.616.75-4.427.113-.228.229-.463.344-.737A12.548,12.548,0,0,0,50,44.013v-7.37a3.6,3.6,0,0,0-3.156-3.626,3.55,3.55,0,0,0-2.406.655,3.461,3.461,0,0,0-2.594-1.655,3.55,3.55,0,0,0-2.406.655,3.461,3.461,0,0,0-2.594-1.655A3.5,3.5,0,0,0,35,31.339V28H49A11.013,11.013,0,0,0,60,17V11A11.013,11.013,0,0,0,49,0ZM35.491,33.39a1.491,1.491,0,0,1,1.162-.383A1.593,1.593,0,0,1,38,34.643v.86a1,1,0,0,0,2,0,1.5,1.5,0,0,1,1.653-1.5A1.593,1.593,0,0,1,43,35.643v.86a1,1,0,0,0,2,0,1.5,1.5,0,0,1,1.653-1.5A1.593,1.593,0,0,1,48,36.643v7.37a10.564,10.564,0,0,1-.753,4.055c-.095.228-.193.424-.288.616C46.466,49.676,46,50.613,46,54v3a1,1,0,0,1-1,1H33a1,1,0,0,1-1-1c0-3.555-1.692-5.181-3.651-7.063-.524-.5-1.065-1.022-1.616-1.617l-.432-.463a13.371,13.371,0,0,1-4.293-9.074A1.633,1.633,0,0,1,23.654,37a1.438,1.438,0,0,1,1.4,1.238,5.967,5.967,0,0,0,3.529,4.672A1,1,0,0,0,30,42V20.645a1.6,1.6,0,0,1,1.341-1.637A1.525,1.525,0,0,1,33,20.5v14a1,1,0,0,0,2,0A1.489,1.489,0,0,1,35.491,33.39ZM58,17a9.01,9.01,0,0,1-9,9H35V20.5a3.538,3.538,0,0,0-3.859-3.482A3.613,3.613,0,0,0,28,20.645V26H11a9.01,9.01,0,0,1-9-9V11a9.01,9.01,0,0,1,9-9H49a9.01,9.01,0,0,1,9,9Z"></path><path d="M15.035,12.174,9.582,8.408A2.3,2.3,0,0,0,6,10.234v7.532a2.3,2.3,0,0,0,3.583,1.826l5.453-3.767a2.21,2.21,0,0,0,0-3.651ZM13.9,14.181l-5.45,3.764c-.153.1-.447.042-.447-.179V10.235c0-.216.295-.284.446-.18L13.9,13.819A.213.213,0,0,1,13.9,14.181Z"></path><path d="M32,6a1,1,0,0,0-1,1v6a1,1,0,0,0,2,0V7A1,1,0,0,0,32,6Z"></path><path d="M41.644,9.234a1,1,0,0,0-1.41.122l-4,4.755A1,1,0,1,0,37.766,15.4l4-4.754A1,1,0,0,0,41.644,9.234Z"></path><path d="M23.766,9.356a1,1,0,1,0-1.532,1.288l4,4.754a1,1,0,1,0,1.532-1.287Z"></path><path d="M25,19H19a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2Z"></path><path d="M45,19H39a1,1,0,0,0,0,2h6a1,1,0,0,0,0-2Z"></path></svg>`,
      
      component: `

<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 60 60" version="1.1" x="0px" y="0px"><title>Tools - Outline - 062 - Toolbox</title><desc>Created with Sketch.</desc><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g fill-rule="nonzero" fill="#000000"><path d="M57,60 L3,60 C1.34389511,59.9981923 0.00180771165,58.6561049 0,57 L0,32 C0.00180771165,30.3438951 1.34389511,29.0018077 3,29 L57,29 C58.6561049,29.0018077 59.9981923,30.3438951 60,32 L60,57 C59.9981923,58.6561049 58.6561049,59.9981923 57,60 L57,60 Z M3,31 C2.44794374,31.0005512 2.00055123,31.4479437 2,32 L2,57 C2.00055123,57.5520563 2.44794374,57.9994488 3,58 L57,58 C57.5520563,57.9994488 57.9994488,57.5520563 58,57 L58,32 C57.9994488,31.4479437 57.5520563,31.0005512 57,31 L3,31 Z"></path><path d="M57,31 L3,31 C2.73476237,31.000069 2.48036837,30.8947343 2.29281703,30.707183 C2.1052657,30.5196316 1.99993101,30.2652376 2,30 L2,7 C2.00180771,5.34389511 3.34389511,4.00180771 5,4 L55,4 C56.6561049,4.00180771 57.9981923,5.34389511 58,7 L58,30 C58.000069,30.2652376 57.8947343,30.5196316 57.707183,30.707183 C57.5196316,30.8947343 57.2652376,31.000069 57,31 Z M4,29 L56,29 L56,7 C55.9994488,6.44794374 55.5520563,6.00055123 55,6 L5,6 C4.44794374,6.00055123 4.00055123,6.44794374 4,7 L4,29 Z"></path><path d="M53,31 L7,31 C6.73476237,31.000069 6.48036837,30.8947343 6.29281703,30.707183 C6.1052657,30.5196316 5.99993101,30.2652376 6,30 L6,9 C5.99993101,8.73476237 6.1052657,8.48036837 6.29281703,8.29281703 C6.48036837,8.1052657 6.73476237,7.99993101 7,8 L53,8 C53.2652376,7.99993101 53.5196316,8.1052657 53.707183,8.29281703 C53.8947343,8.48036837 54.000069,8.73476237 54,9 L54,30 C54.000069,30.2652376 53.8947343,30.5196316 53.707183,30.707183 C53.5196316,30.8947343 53.2652376,31.000069 53,31 L53,31 Z M8,29 L52,29 L52,10 L8,10 L8,29 Z"></path><path d="M35,6 L25,6 C24.7347624,6.00006899 24.4803684,5.8947343 24.292817,5.70718297 C24.1052657,5.51963163 23.999931,5.26523763 24,5 L24,3 C24.0018077,1.34389511 25.3438951,0.00180771165 27,-8.8817842e-16 L33,-8.8817842e-16 C34.6561049,0.00180771165 35.9981923,1.34389511 36,3 L36,5 C36.000069,5.26523763 35.8947343,5.51963163 35.707183,5.70718297 C35.5196316,5.8947343 35.2652376,6.00006899 35,6 Z M26,4 L34,4 L34,3 C33.9994488,2.44794374 33.5520563,2.00055123 33,2 L27,2 C26.4479437,2.00055123 26.0005512,2.44794374 26,3 L26,4 Z"></path><path d="M13,6 L7,6 C6.73476237,6.00006899 6.48036837,5.8947343 6.29281703,5.70718297 C6.1052657,5.51963163 5.99993101,5.26523763 6,5 L6,3 C6.00180771,1.34389511 7.34389511,0.00180771165 9,0 L11,0 C12.6561049,0.00180771165 13.9981923,1.34389511 14,3 L14,5 C14.000069,5.26523763 13.8947343,5.51963163 13.707183,5.70718297 C13.5196316,5.8947343 13.2652376,6.00006899 13,6 Z M8,4 L12,4 L12,3 C11.9994488,2.44794374 11.5520563,2.00055123 11,2 L9,2 C8.44794374,2.00055123 8.00055123,2.44794374 8,3 L8,4 Z"></path><path d="M53,6 L47,6 C46.7347624,6.00006899 46.4803684,5.8947343 46.292817,5.70718297 C46.1052657,5.51963163 45.999931,5.26523763 46,5 L46,3 C46.0018077,1.34389511 47.3438951,0.00180771165 49,0 L51,0 C52.6561049,0.00180771165 53.9981923,1.34389511 54,3 L54,5 C54.000069,5.26523763 53.8947343,5.51963163 53.707183,5.70718297 C53.5196316,5.8947343 53.2652376,6.00006899 53,6 Z M48,4 L52,4 L52,3 C51.9994488,2.44794374 51.5520563,2.00055123 51,2 L49,2 C48.4479437,2.00055123 48.0005512,2.44794374 48,3 L48,4 Z"></path><path d="M11,49 L9,49 C7.34389511,48.9981923 6.00180771,47.6561049 6,46 L6,30 C5.99993101,29.7347624 6.1052657,29.4803684 6.29281703,29.292817 C6.48036837,29.1052657 6.73476237,28.999931 7,29 L13,29 C13.2652376,28.999931 13.5196316,29.1052657 13.707183,29.292817 C13.8947343,29.4803684 14.000069,29.7347624 14,30 L14,46 C13.9981923,47.6561049 12.6561049,48.9981923 11,49 Z M8,31 L8,46 C8.00055123,46.5520563 8.44794374,46.9994488 9,47 L11,47 C11.5520563,46.9994488 11.9994488,46.5520563 12,46 L12,31 L8,31 Z"></path><path d="M13,42 L7,42 C6.44771525,42 6,41.5522847 6,41 C6,40.4477153 6.44771525,40 7,40 L13,40 C13.5522847,40 14,40.4477153 14,41 C14,41.5522847 13.5522847,42 13,42 Z"></path><path d="M13,38 L7,38 C6.44771525,38 6,37.5522847 6,37 C6,36.4477153 6.44771525,36 7,36 L13,36 C13.5522847,36 14,36.4477153 14,37 C14,37.5522847 13.5522847,38 13,38 Z"></path><path d="M51,49 L49,49 C47.3438951,48.9981923 46.0018077,47.6561049 46,46 L46,30 C45.999931,29.7347624 46.1052657,29.4803684 46.292817,29.292817 C46.4803684,29.1052657 46.7347624,28.999931 47,29 L53,29 C53.2652376,28.999931 53.5196316,29.1052657 53.707183,29.292817 C53.8947343,29.4803684 54.000069,29.7347624 54,30 L54,46 C53.9981923,47.6561049 52.6561049,48.9981923 51,49 Z M48,31 L48,46 C48.0005512,46.5520563 48.4479437,46.9994488 49,47 L51,47 C51.5520563,46.9994488 51.9994488,46.5520563 52,46 L52,31 L48,31 Z"></path><path d="M53,42 L47,42 C46.4477153,42 46,41.5522847 46,41 C46,40.4477153 46.4477153,40 47,40 L53,40 C53.5522847,40 54,40.4477153 54,41 C54,41.5522847 53.5522847,42 53,42 Z"></path><path d="M53,38 L47,38 C46.4477153,38 46,37.5522847 46,37 C46,36.4477153 46.4477153,36 47,36 L53,36 C53.5522847,36 54,36.4477153 54,37 C54,37.5522847 53.5522847,38 53,38 Z"></path><path d="M33,42 L27,42 C25.3438951,41.9981923 24.0018077,40.6561049 24,39 L24,30 C23.999931,29.7347624 24.1052657,29.4803684 24.292817,29.292817 C24.4803684,29.1052657 24.7347624,28.999931 25,29 L35,29 C35.2652376,28.999931 35.5196316,29.1052657 35.707183,29.292817 C35.8947343,29.4803684 36.000069,29.7347624 36,30 L36,39 C35.9981923,40.6561049 34.6561049,41.9981923 33,42 Z M26,31 L26,39 C26.0005512,39.5520563 26.4479437,39.9994488 27,40 L33,40 C33.5520563,39.9994488 33.9994488,39.5520563 34,39 L34,31 L26,31 Z"></path><path d="M43.67822,26.1709 C42.6977377,26.1696987 41.7280819,25.9658938 40.83008,25.57227 C40.3236349,25.3514113 40.0921214,24.7618151 40.31298,24.25537 C40.5338387,23.7489249 41.1234349,23.5174113 41.62988,23.73827 C42.9138207,24.2953627 44.3705327,24.2990346 45.6572654,23.7484215 C46.9439982,23.1978084 47.9471396,22.1415269 48.43066,20.82811 L46.145,22.14355 C44.8313412,22.8976336 43.1551331,22.4448168 42.39988,21.13183 L41.39549,19.38867 C40.6506508,18.0766794 41.098402,16.4096831 42.40037,15.64746 L44.9785,14.16992 C43.2254013,13.6988577 41.3541721,14.1991542 40.0700694,15.4822502 C38.7859667,16.7653461 38.2842028,18.6361823 38.75389,20.38965 C38.8979162,20.9225975 38.5828965,21.4714525 38.0500667,21.6159132 C37.5172368,21.7603739 36.9681251,21.4458019 36.82323,20.91309 C36.0501641,18.0428556 37.1544102,14.9955526 39.5867374,13.2868511 C42.0190646,11.5781496 45.260266,11.5727908 47.69823,13.27344 C47.9786228,13.4697495 48.1390937,13.7958618 48.1235539,14.1377917 C48.108014,14.4797216 47.9186237,14.7899335 47.62158,14.96 L43.40137,17.378 C43.0500538,17.5861475 42.9294567,18.0368762 43.12989,18.39265 L44.13428,20.13581 C44.23237,20.3069704 44.3945819,20.4320234 44.5850677,20.483333 C44.7755536,20.5346426 44.9786306,20.5079845 45.14942,20.40925 L49.23142,18.05964 C49.5356381,17.8842507 49.9095696,17.881327 50.2164931,18.0519379 C50.5234165,18.2225488 50.7183274,18.5416774 50.72996,18.89264 C50.7814229,20.7990448 50.0623936,22.6457392 48.7353569,24.0154112 C47.4083201,25.3850831 45.5852789,26.1621313 43.6782,26.17096 L43.67822,26.1709 Z"></path><path d="M29.90967,31 C29.5207028,30.999837 29.1671217,30.7741416 29.0032123,30.4213964 C28.839303,30.0686511 28.8948089,29.6528661 29.14551,29.35547 L37.02539,20.00586 C37.3813541,19.5835555 38.0122655,19.5297759 38.43457,19.88574 C38.8568745,20.2417041 38.9106541,20.8726155 38.55469,21.29492 L30.6748,30.64453 C30.4847868,30.8702249 30.2047006,31.0003495 29.90967,31 L29.90967,31 Z"></path><path d="M36.72949,31 C36.3405228,30.999837 35.9869417,30.7741416 35.8230323,30.4213964 C35.659123,30.0686511 35.7146289,29.6528661 35.96533,29.35547 L40.46533,24.01563 C40.8212941,23.5933255 41.4522055,23.5395459 41.87451,23.89551 C42.2968145,24.2514741 42.3505941,24.8823855 41.99463,25.30469 L37.49463,30.64453 C37.3046146,30.8702276 37.0245241,31.0003524 36.72949,31 L36.72949,31 Z"></path><path d="M15.91113,31 C15.5659408,31.0006546 15.2448763,30.8230429 15.06201,30.53027 L10.76172,23.65039 C9.81192232,22.1363861 9.7427345,20.230473 10.5802551,18.6515865 C11.4177758,17.0727 13.0345546,16.0611082 14.8207211,15.998397 C16.6068876,15.9356858 18.2906307,16.8313984 19.23682,18.34766 L26.188,29.46973 C26.4808628,29.9381475 26.3385475,30.5552872 25.87013,30.84815 C25.4017124,31.1410128 24.7845728,30.9986975 24.49171,30.53028 L17.542,19.41016 C16.6457238,18.030236 14.8165402,17.6098266 13.40772,18.45996 C12.0068848,19.3394207 11.5818846,21.1866105 12.45752,22.58984 L16.7583,29.46972 C16.9507649,29.7779182 16.9609845,30.1662431 16.7849965,30.4841379 C16.6090085,30.8020327 16.2744875,30.9995076 15.91113,31 Z"></path><path d="M20.63086,31 C20.2858049,31.0006408 19.9648935,30.8230113 19.78223,30.53027 L15.40235,23.53027 C15.21158,23.2273949 15.1972708,22.8456456 15.3648318,22.5293391 C15.5323928,22.2130325 15.8562586,22.010429 16.2139946,21.9981208 C16.5717306,21.9858127 16.9087555,22.1656779 17.09766,22.46972 L21.47754,29.46972 C21.6701842,29.7778094 21.6805638,30.166148 21.5046525,30.4840883 C21.3287411,30.8020286 20.9942202,30.9995391 20.63086,31 L20.63086,31 Z"></path></g></g></svg>`,
      
      form: `

<svg xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 64 64" x="0px" y="0px"><path d="M62,4H2A1,1,0,0,0,1,5V45a1,1,0,0,0,1,1H24.67L21,58.73a1,1,0,0,0,.42,1.11,1,1,0,0,0,1.2-.09L38.38,46H62a1,1,0,0,0,1-1V5A1,1,0,0,0,62,4ZM61,44H38a1,1,0,0,0-.66.25L23.89,56,27,45.27a1,1,0,0,0-.16-.87A1,1,0,0,0,26,44H3V6H61Z"></path><path d="M7,15H21a1,1,0,0,0,0-2H7a1,1,0,0,0,0,2Z"></path><path d="M7,22H25a1,1,0,0,0,0-2H7a1,1,0,0,0,0,2Z"></path><path d="M7,29H32a1,1,0,0,0,0-2H7a1,1,0,0,0,0,2Z"></path><path d="M7,36H41a1,1,0,0,0,0-2H7a1,1,0,0,0,0,2Z"></path><path d="M36.2,31.8a1,1,0,0,0,.45-.11l5.65-2.82a1,1,0,0,0,.26-.19L56.71,14.54h0l1.41-1.42a3,3,0,0,0-4.24-4.24L38.32,24.44a1.18,1.18,0,0,0-.19.25l-2.82,5.66a1,1,0,0,0,.18,1.16A1,1,0,0,0,36.2,31.8ZM55.29,10.29a1,1,0,0,1,1.42,0,1,1,0,0,1,0,1.42l-.71.7L54.59,11Zm-2.12,2.12,1.42,1.42L41.86,26.56l-1.42-1.42ZM39.3,26.83l.87.87-1.73.86Z"></path></svg>
`,
      
      float: {
        left: `<svg style="width: 26px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" viewBox="0 0 24 24" style="enable-background:new 0 0 24 24;" xml:space="preserve"><g><path d="M0.5,14h12c0.276,0,0.5-0.224,0.5-0.5v-12C13,1.224,12.776,1,12.5,1h-12C0.224,1,0,1.224,0,1.5v12   C0,13.776,0.224,14,0.5,14z M9,3.5c0.828,0,1.5,0.672,1.5,1.5S9.828,6.5,9,6.5S7.5,5.828,7.5,5S8.172,3.5,9,3.5z M2.026,11.342l2-6   C4.09,5.15,4.263,5.016,4.464,5.001C4.67,4.985,4.857,5.095,4.947,5.276l1.69,3.379l1.509-1.509   c0.113-0.114,0.271-0.167,0.434-0.14c0.159,0.026,0.295,0.126,0.367,0.27l2,4c0.078,0.155,0.069,0.339-0.022,0.486   S10.673,12,10.5,12h-8c-0.161,0-0.312-0.077-0.406-0.208C2,11.662,1.975,11.494,2.026,11.342z"></path><path d="M15.5,2h8C23.776,2,24,1.776,24,1.5S23.776,1,23.5,1h-8C15.224,1,15,1.224,15,1.5S15.224,2,15.5,2z"></path><path d="M23.5,4h-8C15.224,4,15,4.224,15,4.5S15.224,5,15.5,5h8C23.776,5,24,4.776,24,4.5S23.776,4,23.5,4z"></path><path d="M23.5,7h-8C15.224,7,15,7.224,15,7.5S15.224,8,15.5,8h8C23.776,8,24,7.776,24,7.5S23.776,7,23.5,7z"></path><path d="M23.5,10h-8c-0.276,0-0.5,0.224-0.5,0.5s0.224,0.5,0.5,0.5h8c0.276,0,0.5-0.224,0.5-0.5S23.776,10,23.5,10z"></path><path d="M23.5,13h-8c-0.276,0-0.5,0.224-0.5,0.5s0.224,0.5,0.5,0.5h8c0.276,0,0.5-0.224,0.5-0.5S23.776,13,23.5,13z"></path><path d="M23.5,16h-23C0.224,16,0,16.224,0,16.5S0.224,17,0.5,17h23c0.276,0,0.5-0.224,0.5-0.5S23.776,16,23.5,16z"></path><path d="M23.5,19h-23C0.224,19,0,19.224,0,19.5S0.224,20,0.5,20h23c0.276,0,0.5-0.224,0.5-0.5S23.776,19,23.5,19z"></path><path d="M23.5,22h-23C0.224,22,0,22.224,0,22.5S0.224,23,0.5,23h23c0.276,0,0.5-0.224,0.5-0.5S23.776,22,23.5,22z"></path></g></svg>`,
        right: `<svg style="width: 26px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" viewBox="0 0 24 24" style="enable-background:new 0 0 24 24;" xml:space="preserve"><g><path d="M0.5,2h8C8.776,2,9,1.776,9,1.5S8.776,1,8.5,1h-8C0.224,1,0,1.224,0,1.5S0.224,2,0.5,2z"></path><path d="M0.5,5h8C8.776,5,9,4.776,9,4.5S8.776,4,8.5,4h-8C0.224,4,0,4.224,0,4.5S0.224,5,0.5,5z"></path><path d="M0.5,8h8C8.776,8,9,7.776,9,7.5S8.776,7,8.5,7h-8C0.224,7,0,7.224,0,7.5S0.224,8,0.5,8z"></path><path d="M0.5,11h8C8.776,11,9,10.776,9,10.5S8.776,10,8.5,10h-8C0.224,10,0,10.224,0,10.5S0.224,11,0.5,11z"></path><path d="M0.5,14h8C8.776,14,9,13.776,9,13.5S8.776,13,8.5,13h-8C0.224,13,0,13.224,0,13.5S0.224,14,0.5,14z"></path><path d="M23.5,16h-23C0.224,16,0,16.224,0,16.5S0.224,17,0.5,17h23c0.276,0,0.5-0.224,0.5-0.5S23.776,16,23.5,16z"></path><path d="M23.5,19h-23C0.224,19,0,19.224,0,19.5S0.224,20,0.5,20h23c0.276,0,0.5-0.224,0.5-0.5S23.776,19,23.5,19z"></path><path d="M23.5,22h-23C0.224,22,0,22.224,0,22.5S0.224,23,0.5,23h23c0.276,0,0.5-0.224,0.5-0.5S23.776,22,23.5,22z"></path><path d="M23.5,1h-12C11.224,1,11,1.224,11,1.5v12c0,0.276,0.224,0.5,0.5,0.5h12c0.276,0,0.5-0.224,0.5-0.5v-12   C24,1.224,23.776,1,23.5,1z M20,3.5c0.828,0,1.5,0.672,1.5,1.5S20.828,6.5,20,6.5S18.5,5.828,18.5,5S19.172,3.5,20,3.5z    M21.925,11.763C21.834,11.91,21.673,12,21.5,12h-8c-0.161,0-0.312-0.077-0.406-0.208c-0.094-0.13-0.12-0.298-0.068-0.451l2-6   c0.064-0.192,0.237-0.326,0.438-0.34c0.206-0.016,0.393,0.094,0.483,0.275l1.69,3.379l1.509-1.509   c0.113-0.114,0.271-0.167,0.434-0.14c0.159,0.026,0.295,0.126,0.367,0.27l2,4C22.025,11.431,22.017,11.615,21.925,11.763z"></path></g></svg>`,
        none: `<svg style="width: 26px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" viewBox="0 0 24 24" style="enable-background:new 0 0 24 24;" xml:space="preserve"><g><path d="M23,16H1c-0.276,0-0.5,0.224-0.5,0.5S0.724,17,1,17h22c0.276,0,0.5-0.224,0.5-0.5S23.276,16,23,16z"></path><path d="M23,19H1c-0.276,0-0.5,0.224-0.5,0.5S0.724,20,1,20h22c0.276,0,0.5-0.224,0.5-0.5S23.276,19,23,19z"></path><path d="M23,22H1c-0.276,0-0.5,0.224-0.5,0.5S0.724,23,1,23h22c0.276,0,0.5-0.224,0.5-0.5S23.276,22,23,22z"></path><path d="M6,14h12c0.276,0,0.5-0.224,0.5-0.5v-12C18.5,1.224,18.276,1,18,1H6C5.724,1,5.5,1.224,5.5,1.5v12   C5.5,13.776,5.724,14,6,14z M14.5,3.5C15.328,3.5,16,4.172,16,5s-0.672,1.5-1.5,1.5S13,5.828,13,5S13.672,3.5,14.5,3.5z    M7.526,11.342l2-6C9.59,5.15,9.763,5.016,9.964,5.001c0.206-0.016,0.393,0.094,0.483,0.275l1.69,3.379l1.509-1.509   c0.113-0.114,0.271-0.167,0.434-0.14c0.159,0.026,0.295,0.126,0.367,0.27l2,4c0.078,0.155,0.069,0.339-0.022,0.486S16.173,12,16,12   H8c-0.161,0-0.312-0.077-0.406-0.208C7.5,11.662,7.475,11.494,7.526,11.342z"></path></g></svg>`,
      },
    }),

    editContextAllows () {
      let allows = {
        typography: true,
        insertion: true,
        blockAlignment: false,
        boxLayout: false,
        elevation: false,
      };

      if (this.editContext === 'p-buttons') {
        allows.typography = false;
        allows.insertion = false;
        allows.blockAlignment = false;
        allows.boxLayout = true;
        allows.elevation = false;
      }
      
      if (this.editContext === 'image') {
        allows.typography = false;
        allows.insertion = false;
        allows.blockAlignment = true;
        allows.boxLayout = true;
        allows.elevation = true;
      }

      if (this.customComponentTags.indexOf(this.editContext) !== -1) {
        allows.typography = false;
        allows.insertion = false;
        allows.blockAlignment = true;
        allows.boxLayout = true;
        allows.elevation = true;
      }
      
      return allows;
    },
    
    boxLayoutSelectionSelected () {
      return this.boxLayoutSelection.selected;
    },
    
    boxLayoutSelection () {
      return {
        type: boxLayoutSelection.selected?.type?.name,
        ...boxLayoutSelection
      };
    },
    
    editContext () {
      const { node } = this.editor.state.selection;
      return node?.type?.name;
    },
    
    images () {
      return this.$store.state.repo.images;
    },
  },
  
  data () {
    return {
      elevations: [
        {label: "Shallow", value: '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)'},
        {label: "Medium", value: '0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)'},
        {label: "Raised", value: '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)'},
        {label: "Extra raised", value: '0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)'},
        {label: 'From bottom right', value: 'black 5px 5px 5px 0px'},
      ],
      editor: null,
      submenu: null,
      customComponentTags: [],
    };
  },

  methods: {

    toggleSubmenu (type) {
      if (this.submenu === type) {
        this.submenu = null;
      } else {
        this.submenu = type;
      }
    },
    
    async setInternalLink () {
      const previousUrl = this.editor.getAttributes('link').href;
      let link = await openDialog(DialogAddLink, { previousUrl });
      if (!link) return;
      
      if (link.to) {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: link.to === 'index' ? '/' : `/${link.to}` })
          .run();
        return;
      }
      
      if (link.href === '') {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run();
        return;
      }

      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: link.href, target: '_blank' })
        .run();
    },
    
    srcToPublicId: (src, separator) => src.split('/').slice(-2).join(separator || ':'),
    
    clearBoxLayoutSelection () {
      boxLayoutSelection.selecting = false;
      boxLayoutSelection.selected = null;
    },
    
    getBoxLayout (node) {
      return {...node.attrs};
    },
    
    updateBoxLayout (value) {
      this.editor.chain().updateBoxLayout(value).run();
    },
        
    async editImage () {
      const { node } = this.editor.state.selection;
      if (node?.type?.name !== 'image') return;

      const image = node.attrs.src,
            metadata = this.images[this.srcToPublicId(image)] || {};
      if (!metadata.public_id) return;
      
      const { src } = await openDialog(CloudinaryImageEditor, {
        imageId: metadata.public_id,
        originalWidth: metadata.width,
        originalHeight: metadata.height,
        value: image,
      });

      this.editor.commands.updateAttributes('image', { src });
    },

    editBoxLayout () {
      boxLayoutSelection.selecting = true;
    },

    async addButtons () {

      let prevAttrs = {},
          prev = null;
      if (this.editContext === 'p-buttons') {
        prevAttrs = this.editor.state.selection.node.attrs;
        prev = prevAttrs[':links'];
      }

      if (prev) {
        let val = prev;
        if (typeof val === 'string' && !val.startsWith('"') && !val.startsWith("'")) {
          if (val.indexOf('"') !== -1) {
            val = `'${val}'`;
          } else {
            val = `"${val}"`;
          }
        }
        let props = `:links=${val}`;

        let cmp = (new Vue({
          template: `<p-buttons ref="grab-me" ${props}></p-buttons>`,
        })).$mount();

        prev = cmp.$refs['grab-me']['links'];
      }

      const result = await openDialog(DialogAddPButtons, { links: prev, initial: prevAttrs || {} });
      if (!result || !result.links) {
        return;
      }

      let links = '[' + result.links.map(o => `{'href': '${o.href}', 'text': '${o.text}'}`).join(',\n') + ']';
      this.editor.chain().focus()[`setPButtons`]({
        ...prevAttrs,
        ':links': links,
        size: result.width,
        'button-color': result.buttonColor,
        'button-text-color': result.buttonTextColor,
        'button-color__hover': result.buttonColorHover,
        'button-text-color__hover': result.buttonTextColorHover,
      }).run();
    },
    
    async addForm (name) {
      let props = {};
      if (!name) {
        const result = await openDialog(DialogAddUserDefinedComponent, { type: 'form' });
        name = result.name;
        props = result.props;
      }
      this.editor.chain().focus()[`setUserDefinedComponent-${name}`]({
        marginLeft: 'auto', marginRight: 'auto', ...props
      }).run();
    },

    async addUserDefinedComponent (name) {
      let props = {};
      const result  = await openDialog(DialogAddUserDefinedComponent, { preselected: name });
      name = result.name;
      props = result.props;
      if (name) {
        this.editor.chain().focus()[`setUserDefinedComponent-${name}`]({
          marginLeft: 'auto', marginRight: 'auto', ...props
        }).run();
      }
    },

    async addImageFromLibrary () {
      const metadata = await openDialog(ImageLibrary, { initialTab: 'tab-library', closeOnAdd: true });
      
      this.image = {
        src: `https://res.cloudinary.com/pies/image/upload/${metadata.public_id}`,
        info: metadata,
      };
      this.editor.chain().focus().setImage({ src: this.image.src }).run();
    },
  },
  
  watch: {

    async boxLayoutSelectionSelected (node) {
      if (!node) return;
      const value = await openDialog(BoxLayout, {
        value: this.getBoxLayout(node),
      });
      this.editor.chain().updateBoxLayout(value).run();
      this.clearBoxLayoutSelection();
    },
    
    value(value) {
      const isSame = this.editor.getHTML() === value

      if (isSame) {
        return
      }

      this.editor.commands.setContent(this.value, false)
    },
  },
  
  mounted () {

    let customComponents = Object.values(this.$store.state.repo.config.components)
          .filter(c => c.type === 'user-defined' || c.type === 'form')
        .map(({ name, props }) => UserDefinedComponentFactory(name, props || []));

    let customComponentTags = Object.values(this.$store.state.repo.config.components)
          .filter(c => c.type === 'user-defined' || c.type === 'form')
          .map(({ name }) => name);
    
    this.customComponentTags = customComponentTags;
    
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        ClearFormatting,
        BoxLayoutExtension.configure({
          types: ['heading', 'paragraph', 'image', 'p-buttons', ...customComponentTags],
          blockTypes: ['image', 'p-buttons', ...customComponentTags],
        }),
        PButtons,
        CustomImage,
        ...customComponents,
        CustomTextAlign.configure({
          types: ['heading', 'paragraph'],
          blockTypes: ['image', ...customComponentTags],
          defaultAlignment: '',
        }),
        TextStyle,
        Color,
        FontSize,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: null,
            rel: null,
          },
        }),
      ],

      onUpdate: () => {
        this.$emit('input', this.editor.getHTML().replace(/slot-template/g, 'template'));
      },
    })
  },

  beforeDestroy() {
    this.editor.destroy()
  },
}
</script>

<style scoped>
.css-value-submenu {
  min-width: 340px;
  padding-left: 1rem;
}
.css-value-submenu >>> .row {
  width: 250px;
}
.css-value-submenu >>> .box-layout-input .form-control {
  height: 26px;
}
.css-value-submenu >>> .box-layout-input select.form-control {
  padding-top: 0;
  padding-bottom: 0;
}
.css-value-submenu >>> .btn {
  height: 26px;
  display: flex;
  align-items: center;
}

.color-container {
  position: relative;
}
.color-preview {
  width: 17px;
  height: 17px;
  position: relative;
  border: 1px solid black;
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 15%), inset 0 0 4px rgb(0 0 0 / 25%);
}
.color-preview .color-checkerboard {
  background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAAXNSR0IArs4c6QAAADBJREFUOE9jfPbs2X8GPEBSUhKfNAPjqAHDIgz+//+PNx08f/4cfzoYNYCBceiHAQC5flV5JzgrxQAAAABJRU5ErkJggg==");
}
>>> .color-container .color-preview {
  width: 17px;
  height: 17px;
}
.color-preview > div {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.editor {
  border: 3px solid black;
  border-radius: 15px;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-around;
  border-bottom: 3px solid black;
}
.toolbar button {
  -webkit-appearance: none;
  background: transparent;
  border: none;
  padding: 10px 5px;
}
.toolbar .button-set {
  position: relative;
  display: inline-block;
}
.toolbar .button-set .submenu {
  position: absolute;
  left: -5px;
  border-radius: 15px;
  background: white;
  border: 3px solid black;
  border-top-color: transparent;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  z-index: 99;
  display: flex;
  height: 0;
  width: 0;
  opacity: 0;
  overflow: hidden;
  transition: all .1s ease;
}
.toolbar .button-set .submenu.shown {
  opacity: 1;
  flex-direction: column;
  height: auto;
  width: 50px;
  align-items: center;
  border-radius: 0;
}
.toolbar .button-set .submenu.submenu-wide {
  width: 150px;
}

.toolbar .button-set button.is-active {
  background: #eee;
  font-weight: bold;
}

.toolbar .button-set .submenu button.is-active {
  width: 100%;
  background: #eee;
  font-weight: bold;
}
.toolbar .button-set .submenu.shown.horizontal {
  opacity: 1;
  height: 46.39px;
}
</style>
<style>
.ProseMirror {
  padding: 1rem 1.5rem;
  max-height: 60vh;
  overflow: scroll;
}
.ProseMirror img {
  max-width: 100%;
}
.ProseMirror:focus-visible {
  outline: none !important;
}
.box-layout-selecting .ProseMirror {
  cursor: pointer;
}
.box-layout-selecting .ProseMirror h1,
.box-layout-selecting .ProseMirror h2,
.box-layout-selecting .ProseMirror h3,
.box-layout-selecting .ProseMirror h4,
.box-layout-selecting .ProseMirror h5,
.box-layout-selecting .ProseMirror h6,
.box-layout-selecting .ProseMirror p,
.box-layout-selecting .ProseMirror div[data-type^="custom-component"],
.box-layout-selecting .ProseMirror img {
  border: 2px dashed lightblue;
}
.box-layout-selecting .ProseMirror h1:hover,
.box-layout-selecting .ProseMirror h2:hover,
.box-layout-selecting .ProseMirror h3:hover,
.box-layout-selecting .ProseMirror h4:hover,
.box-layout-selecting .ProseMirror h5:hover,
.box-layout-selecting .ProseMirror h6:hover,
.box-layout-selecting .ProseMirror p:hover,
.box-layout-selecting .ProseMirror div[data-type^="custom-component"]:hover,
.box-layout-selecting .ProseMirror img:hover {
  border: 2px dashed yellow;
}

.ProseMirror,
.ProseMirror h1,
.ProseMirror h2,
.ProseMirror h3,
.ProseMirror h4,
.ProseMirror h5,
.ProseMirror h6 {
  text-align: initial;
}
[data-type=p-form] {
  border: 1px solid black;
  box-shadow: 0 1rem 3rem rgb(0 0 0 / 18%);
  margin-bottom: 2rem;
  padding: 1rem;
  text-align: center;
}
[data-type^="custom-component"].ProseMirror-selectednode,
img.ProseMirror-selectednode {
  outline: 3px solid #68CEF8;
}
</style>
<style>
img.float-left {
  float: left;
  margin-right: 2rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
  max-width: 30%;
}
img.float-none {
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 2rem;
  margin-bottom: 2rem;
}
img.float-right {
  float: right;
  margin-left: 2rem;
  margin-top: 2rem;  
  margin-bottom: 2rem;
  max-width: 30%;  
}
p + img.float-left,
p + img.float-none,
p + img.float-right {
  margin-top: 0;
}
</style>
